export const TRACKS = [
  { id: "prompt-eng", title: "Prompt Engineering", subtitle: "From zero to power-user", level: "Beginner → Intermediate", minutes: 55, lessons: 6, color: "terracotta", glyph: "✳" },
  { id: "claude-code", title: "Claude Code", subtitle: "Agentic coding with Claude in your terminal", level: "Intermediate", minutes: 80, lessons: 8, color: "ink", glyph: "⌘" },
  { id: "design-with-claude", title: "Design with Claude", subtitle: "From brief to polished mock", level: "All levels", minutes: 45, lessons: 5, color: "gold", glyph: "◆" },
  { id: "agentic-patterns", title: "Agentic Patterns", subtitle: "Tools, loops, evals", level: "Advanced", minutes: 110, lessons: 10, color: "gem", glyph: "◎" },
  { id: "writing-with-ai", title: "Writing with AI", subtitle: "Voice, edit, ship", level: "Beginner", minutes: 40, lessons: 5, color: "moss", glyph: "✎" },
  { id: "model-evals", title: "Model Evals & Safety", subtitle: "Judge, test, guard", level: "Advanced", minutes: 90, lessons: 7, color: "ink", glyph: "◈" },
];

export const SKILL_TREE = [
  {
    unit: 1,
    title: "Foundations",
    caption: "How prompts work",
    nodes: [
      { id: "n1", title: "What a prompt really is", kind: "lesson", status: "done", xp: 20 },
      { id: "n2", title: "Clear instructions", kind: "lesson", status: "done", xp: 25 },
      { id: "n3", title: "Checkpoint: Foundations", kind: "checkpoint", status: "current", xp: 50 },
    ],
  },
  {
    unit: 2,
    title: "Structure",
    caption: "Shape the reply",
    nodes: [
      { id: "n4", title: "Roles & system prompts", kind: "lesson", status: "locked", xp: 25 },
      { id: "n5", title: "Output formats: JSON, tables, XML", kind: "practice", status: "locked", xp: 30 },
      { id: "n6", title: "Few-shot examples", kind: "lesson", status: "locked", xp: 25 },
      { id: "n7", title: "Boss: Shape the reply", kind: "boss", status: "locked", xp: 80 },
    ],
  },
  {
    unit: 3,
    title: "Reasoning",
    caption: "Think before answering",
    nodes: [
      { id: "n8", title: "Step-by-step thinking", kind: "lesson", status: "locked", xp: 30 },
      { id: "n9", title: "Self-critique & iteration", kind: "practice", status: "locked", xp: 35 },
      { id: "n10", title: "Decomposition", kind: "lesson", status: "locked", xp: 30 },
    ],
  },
  {
    unit: 4,
    title: "Mastery",
    caption: "Ship it",
    nodes: [
      { id: "n11", title: "Prompt anti-patterns", kind: "lesson", status: "locked", xp: 25 },
      { id: "n12", title: "Real-world prompts", kind: "practice", status: "locked", xp: 40 },
      { id: "n13", title: "Final challenge", kind: "final", status: "locked", xp: 150 },
    ],
  },
];

export const LESSON_CONTENT = {
  id: "n3",
  unit: "Unit 1 · Foundations",
  title: "Clear instructions",
  readingMinutes: 4,
  sections: [
    { kind: "h", text: "The single biggest upgrade to your prompts" },
    { kind: "p", text: "Most bad outputs aren't the model's fault — they come from instructions that the model has to guess at. The fix is rarely more words. It's sharper words." },
    { kind: "p", text: "A clear prompt tells Claude three things: who is asking, what the output must look like, and which parts of the task are non-negotiable. That's it. Everything else is decoration." },
    { kind: "callout", label: "Rule of thumb", text: "If a smart intern on their first day couldn't follow your prompt, Claude can't either. Write for that intern." },
    { kind: "h", text: "Compare these two prompts" },
    {
      kind: "compare",
      bad: "write a summary of the meeting",
      good: "Summarize the meeting transcript below in 5 bullet points.\nAudience: engineers who weren't there.\nFor each bullet, include: topic, decision, owner.\nIf a decision is missing, say \"undecided\" — don't guess.",
    },
    { kind: "p", text: "The second prompt names the audience, fixes the format, lists the required fields, and tells the model how to handle missing information. No magic — just specificity." },
    { kind: "h", text: "Four moves that almost always help" },
    {
      kind: "list",
      items: [
        { b: "Name the audience.", r: "\"for a non-technical exec\" changes vocabulary, length, and examples." },
        { b: "Fix the shape of the output.", r: "Bullets? Table? JSON? Decide before the model does." },
        { b: "State the hard constraints.", r: "Length, tone, what to include, what to never include." },
        { b: "Say what to do with missing info.", r: "Ask, skip, flag — otherwise the model fills gaps with guesses." },
      ],
    },
    { kind: "tip", text: "You'll earn 💎 10 gems every time you rewrite a vague prompt into a specific one during exercises." },
  ],
};

export const EXERCISES = [
  {
    id: "e1",
    type: "mcq",
    prompt: "Which prompt is most likely to produce a reliable, reusable output?",
    options: [
      { id: "a", text: "Write something good about our Q3 results." },
      { id: "b", text: "Summarize Q3 results in 3 bullets for investors. Each bullet: metric, value, YoY change. If a value is missing, mark it TBD." },
      { id: "c", text: "You're the best CFO ever. Make Q3 sound amazing." },
      { id: "d", text: "Q3 summary please, thanks!" },
    ],
    correct: "b",
    explain: "Option B names the audience (investors), fixes the shape (3 bullets with 3 fields), and tells the model how to handle missing data. That's the clear-instructions trifecta.",
  },
  {
    id: "e2",
    type: "fill",
    prompt: "Fill in the blanks to turn this vague prompt into a sharp one.",
    template: [
      { kind: "t", v: "Summarize the article below for " },
      { kind: "b", id: "audience", hint: "audience", answer: "engineers" },
      { kind: "t", v: ".\nFormat: " },
      { kind: "b", id: "format", hint: "format", answer: "5 bullets" },
      { kind: "t", v: ".\nIf a claim isn't supported by the article, " },
      { kind: "b", id: "missing", hint: "handle missing", answer: "skip it" },
      { kind: "t", v: "." },
    ],
    accept: {
      audience: ["engineers", "a technical audience", "developers"],
      format: ["5 bullets", "five bullet points", "bullet points", "bullets"],
      missing: ["skip it", "skip", "omit it", "don't include it", "mark it as unsupported"],
    },
    explain: "You could fill in other reasonable values — what matters is that each blank removes ambiguity.",
  },
  {
    id: "e3",
    type: "code",
    prompt: "Add an output schema to this prompt so Claude always returns parseable JSON.",
    starter: `You are a support triage assistant.
Given a customer message, classify urgency.

Customer message:
"""
{{ message }}
"""

Return: __________`,
    target: `You are a support triage assistant.
Given a customer message, classify urgency.

Customer message:
"""
{{ message }}
"""

Return: JSON with fields { "urgency": "low" | "med" | "high", "reason": string }`,
    hint: "The blank should describe the exact shape of the JSON — keys and allowed values.",
    explain: "When you fix the shape with an explicit schema, downstream code can trust the result without reparsing prose.",
  },
  {
    id: "e4",
    type: "short",
    prompt: "In your own words (2–3 sentences), explain why stating \"what to do with missing info\" matters in a prompt.",
    rubric: "Mentions that models will otherwise hallucinate / fill gaps / guess, AND that naming the behavior (ask / skip / flag / mark TBD) gives predictable output.",
    explain: "Great answers touch on both halves: the failure mode (guessing), and the fix (an explicit fallback rule).",
  },
  {
    id: "e5",
    type: "mcq",
    prompt: "A prompt says \"keep it short.\" What's the problem?",
    options: [
      { id: "a", text: "Nothing — Claude understands \"short\" perfectly." },
      { id: "b", text: "\"Short\" is undefined — sentence? paragraph? 50 words? 500?" },
      { id: "c", text: "You can't ask Claude to be concise." },
      { id: "d", text: "Short prompts produce long answers." },
    ],
    correct: "b",
    explain: "Soft words like \"short,\" \"soon,\" \"casual\" leave too much to interpretation. Replace them with measurable constraints: \"under 80 words,\" \"2 sentences,\" \"title-case.\"",
  },
];
